<template>
  <div class="flex items-center gap-2">
    <span class="truncate">{{ value }}</span>
    <button
      v-tooltip="tooltipText"
      type="button"
      :class="[
        'shrink-0 flex items-center justify-center rounded w-6 h-6',
        isResubmitDisabled
          ? 'opacity-50 cursor-not-allowed text-label'
          : 'text-label hover:bg-surface-base',
      ]"
      :aria-disabled="isResubmitDisabled"
      data-cy="resubmit-placement"
      @click.stop="handleResubmit"
    >
      <icon-base :icon="IconRestart" class="size-4" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useDeals } from "@/hooks/deals";

import { useResubmitPlacementMutation } from "@/hooks/queries/applications";

import IconRestart from "@/components/icons/IconRestart.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IPlacementTableRow } from "@/models/offers";

const { value, model } = defineProps<{
  value?: string;
  model: IPlacementTableRow;
}>();

const { t } = useI18n();
const { dispatch } = useStore();
const { activeDeal } = useDeals();

const { mutate: resubmitPlacement, isPending } = useResubmitPlacementMutation();

const tooltipText = computed(() => {
  if (model.can_resubmit) {
    return t("DEALS.PLACEMENT.RESUBMIT_TOOLTIP_SUPPORTED");
  }
  return t("DEALS.PLACEMENT.RESUBMIT_TOOLTIP_UNSUPPORTED");
});

const isResubmitDisabled = computed(() => {
  return !model.can_resubmit || isPending.value;
});

const handleResubmit = () => {
  if (isResubmitDisabled.value) {
    return;
  }

  const applicationId = activeDeal.value?.id;

  if (!applicationId) {
    return;
  }

  const productName = value || model.offer_template;
  const funderName = model.funder_full_name;

  resubmitPlacement(
    {
      applicationId,
      placementId: model.placement_id,
      productName,
      funderName,
    },
    {
      onSuccess: () => {
        dispatch("applications/getPlacements");
      },
    }
  );
};
</script>
